import type { ContratCxpiDTO } from '@/features/api/extranet/dto/Demande/CXPI'
import type { ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
import { CXPI_ACTIONS } from '@/features/interface/demande/cxpi/useDemandeCXPIActions'
import { useDemandeCXPIActions } from '@/features/interface/demande/cxpi/useDemandeCXPIActions.ts'
import type { SetupDemande } from '@/features/interface/demande/shared/components/DemandeContent/DemandeContent.types.ts'
import { type Component, provide } from 'vue'

import {
  CXPI_INTERRUPTIONS_ACTIONS,
  useInterruptionsActions,
} from '@/features/interface/demande/cxpi/steps/contrat/useInterruptionsActions.ts'
import {
  CXPI_MODULES_ACTIONS,
  useModuleActions,
} from '@/features/interface/demande/cxpi/steps/formation/useModuleActions.ts'
import { useReservationDemandeCXPIStore } from '@/features/interface/demande/cxpi/useCXPIStore.ts'
import ContratStep from './steps/contrat/ContratStep.vue'
import DocumentsStep from './steps/documents/DocumentsStep.vue'
import EmployeurStep from './steps/employeur/EmployeurStep.vue'
import FormationStep from './steps/formation/FormationStep.vue'
import IntroductionStep from './steps/introduction/IntroductionStep.vue'
import SyntheseStep from './steps/synthese/SyntheseStep.vue'

type TabKey = 'Introduction' | 'Employeur' | 'Contrat' | 'Formation' | 'Synthèse' | 'Documents'

const TABS_VUES: Record<TabKey, Component> = {
  Introduction: IntroductionStep,
  Employeur: EmployeurStep,
  Contrat: ContratStep,
  Formation: FormationStep,
  Synthèse: SyntheseStep,
  Documents: DocumentsStep,
} as const

const TABS_ERR_CONFIG: ErreurSummaryConfiguration<ContratCxpiDTO> = {
  Introduction: {
    include: (state) => [state.validationNotice],
  },
  Employeur: {
    includeDeep: (state) => [state.employeur],
  },
  Contrat: {
    includeDeep: (state) => [state.contrat],
  },
  Formation: {
    includeDeep: (state) => [state.formation, state.mission],
  },
  Synthèse: {
    include: (state) => [
      state.lieuSignature,
      state.dateSignature,
      state.commentaire,
      state.acceptationCg,
      state.attestationSurHonneur
    ],
  },
  Documents: {
    includeDeep: (state) => [state.fichiers],
    include: (state) => [state.attestationPiecesJustificatives],
  },
} as const

export const SetupDrfDemandeCXPI: SetupDemande<'MODEL_CXPI', TabKey> = {
  useStore: useReservationDemandeCXPIStore,
  injectActions: () => {
    const store = useReservationDemandeCXPIStore()
    const actions = useDemandeCXPIActions(store)
    provide(CXPI_ACTIONS, actions)
    return actions
  },
  tabFirst: 'Introduction',
  tabsVue: TABS_VUES,
  tabsErrorConfig: () => TABS_ERR_CONFIG,
  onInititalize: () => {
    const store = useReservationDemandeCXPIStore()
    provide(CXPI_MODULES_ACTIONS, useModuleActions(store))
    provide(CXPI_INTERRUPTIONS_ACTIONS, useInterruptionsActions(store))
  },
} as const
